import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ListDetail } from "@/components/ListDetail";
import { RowListSkeleton } from "@/components/RowListSkeleton";
import { StandaardSpeelschemaGrid } from "./StandaardSpeelschemaGrid";
import {
  findStandaardSpeelschema,
  useStamKlassen,
  useStamNiveaus,
  useStandaardSpeelschemas,
  type KlasseStam,
  type NiveauStam,
} from "./useStamgegevens";

/**
 * Keyed on the niveau, because a wedstrijddag is at exactly one and the same klasse runs a
 * different grid at each (ADR-0039). Its own screen: on the Niveaus page it went unfound.
 */
export function Speelschemas({
  niveauId,
  onKies,
  tab,
  onKiesTab,
}: {
  niveauId: string | undefined;
  onKies: (id?: string) => void;
  /** Which klasse's grid is open, or `undefined` for the first (ADR-0064). */
  tab: string | undefined;
  onKiesTab: (tab: string) => void;
}) {
  const { t } = useTranslation(["stamgegevens", "common"]);
  const { data: niveaus, isPending } = useStamNiveaus();
  const niveau = niveaus?.find((n) => n.id === niveauId);

  return (
    <ListDetail
      selected={Boolean(niveau)}
      backLabel={t("speelschemas.terug")}
      onBack={() => onKies()}
      list={
        <Card>
          <CardContent className="space-y-3 pt-6">
            {isPending ? (
              <RowListSkeleton />
            ) : (
              <ul className="divide-y">
                {niveaus?.map((rij) => (
                  <li key={rij.id} className="py-1">
                    <button
                      type="button"
                      className={`w-full truncate rounded-md px-2 py-1.5 text-start font-medium ${
                        rij.id === niveau?.id ? "bg-accent" : "hover:bg-accent/50"
                      }`}
                      aria-current={rij.id === niveau?.id ? "true" : undefined}
                      onClick={() => onKies(rij.id)}
                    >
                      {rij.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Nothing is reordered or renamed here - that is the Niveaus screen's job, and
                saying so is cheaper than a beheerder looking for the buttons. */}
            <p className="text-sm text-muted-foreground">{t("speelschemas.hernoemen")}</p>
          </CardContent>
        </Card>
      }
      detail={
        niveau ? (
          <NiveauSpeelschemas key={niveau.id} niveau={niveau} tab={tab} onKiesTab={onKiesTab} />
        ) : (
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">{t("speelschemas.kies")}</p>
            </CardContent>
          </Card>
        )
      }
    />
  );
}

/**
 * One tab per klasse (ADR-0064 rule B); stacked they ran to forty-odd inputs. Every klasse keeps
 * a tab, so the counts wait for their data rather than reading zero while it loads.
 */
function NiveauSpeelschemas({
  niveau,
  tab,
  onKiesTab,
}: {
  niveau: NiveauStam;
  tab: string | undefined;
  onKiesTab: (tab: string) => void;
}) {
  const { t } = useTranslation(["stamgegevens", "common"]);
  const { data: klassen, isPending: klassenPending } = useStamKlassen();
  const { data: speelschemas, isPending: speelschemasPending } = useStandaardSpeelschemas();

  const classes = klassen ?? [];

  // The heading is drawn either way: it names the record the pane is about, and a pane that says
  // nothing at all while it loads reads as a screen that failed.
  const heading = <h2 className="text-lg font-bold leading-none tracking-[-0.02em]">{niveau.name}</h2>;

  if (klassenPending || speelschemasPending || classes.length === 0) {
    return (
      <div className="space-y-4">
        {heading}
        <Card>
          <CardContent className="pt-6">
            <RowListSkeleton />
          </CardContent>
        </Card>
      </div>
    );
  }

  const openTab = bepaalOpenTab(classes, tab);

  return (
    <div className="space-y-4">
      {/* No identity form to pin above the strip the way a wedstrijddag has, so a heading says
          which niveau these grids belong to. */}
      {heading}

      <Tabs value={openTab} onValueChange={onKiesTab}>
        <TabsList aria-label={t("speelschemas.strip")}>
          {classes.map((klasse) => (
            <TabsTrigger key={klasse.id} value={klasse.id}>
              {t("common:tabs.metAantal", {
                label: klasse.name,
                aantal:
                  findStandaardSpeelschema(speelschemas, klasse.id, niveau.id)?.regels.length ?? 0,
              })}
            </TabsTrigger>
          ))}
        </TabsList>

        {classes.map((klasse) => (
          <TabsContent key={klasse.id} value={klasse.id}>
            <Card>
              <CardHeader>
                <CardTitle>
                  {t("speelschemas.kaartTitel", {
                    klasse: klasse.name,
                    niveau: niveau.name.toLowerCase(),
                  })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <StandaardSpeelschemaGrid klasse={klasse} niveau={niveau} />
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

/**
 * A stale klasse - or one deleted since the link was sent - is the first tab rather than an
 * empty pane, the way `pagina` is page 1 rather than nothing chosen.
 */
function bepaalOpenTab(classes: KlasseStam[], tab: string | undefined): string {
  return classes.find((klasse) => klasse.id === tab)?.id ?? classes[0].id;
}
